import { Translation } from "../../hooks/useTranslations";
import { CurrencyString, Entity, Version } from "../../types";
import { Car } from "../cars/types";
import { Subsidiary } from "../subsidiaries/types";
import { User } from "../users/types";

export type FineStatus = "new" | "notified" | "archived";

export type Fine = Entity & {
  verbalNumber: string;
  violationDate: string;
  communicationDate?: string;
  firstNotificationDate?: string;
  amount?: number;
  amountCurrency?: CurrencyString;
  points?: number;
  car: Car;
  driver?: User;
  subsidiary: Subsidiary;
  accountant?: User;
  hrReferent?: User;
  status: FineStatus;
  note?: string;
};

export type FineNoCarDependency = Omit<Fine, "car"> & {
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  };
};

export type FineVersion = Version & {
  verbalNumber: string;
  violationDate: string;
  communicationDate?: string;
  amount?: number;
  amountCurrency?: CurrencyString;
  points?: number;
  car?: Car;
  driver?: User;
  subsidiary?: Subsidiary;
  accountant?: User;
  hrReferent?: User;
  status: FineStatus;
  note?: string;
};

export type UpsertFine = {
  carId: string;
  driverId: string;
  subsidiaryId: string;
  status: FineStatus;
  verbalNumber: string;
  violationDate: string;
  communicationDate?: string;
  amount?: number | null;
  amountCurrency?: CurrencyString;
  points?: number | null;
  accountantId?: string | null;
  hrReferentId?: string | null;
  note?: string;
  currentVersionNumber?: number;
};

export type NotifyFine = {
  // language chosen like for the agreements (subsidiary languages)
  language: string;
  attachmentIds: string[];
  accountantId: string | null;
  hrReferentId: string | null;
};

export type FineNotification = {
  id: string;
  sentBy: User;
  sentAt: string;
};

export type FineStatusType = {
  value: FineStatus;
  label: string;
};

export const getFineStateColor = (status?: FineStatus): "green" | "yellow" | "orange" | "red" | "blue" => {
  switch (status) {
    case "new":
      return "blue";
    case "notified":
      return "green";
    case "archived":
      return "orange";
    default:
      return "blue";
  }
};

export const getFineStatuses = (i18n: Translation): FineStatusType[] => [
  { value: "new", label: i18n.fines.statuses.new },
  { value: "notified", label: i18n.fines.statuses.notified },
  { value: "archived", label: i18n.fines.statuses.archived },
];
